import { Colors, Icon } from '@blueprintjs/core'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { GroupedCharts } from '../components/chart/grouped_charts'
import { Card } from '../components/ui/card'
import type { TableHooks } from '../packages/blueprint-components/src/useTableHooks'
import { adManagerPerformanceGet } from '../util/adManagerPerformanceApi'
import { parseDate } from '../util/dates'
import { money, number, percentage } from '../util/formatters'
import { isNavV2Enabled } from '../util/nav-version'
import { useUser } from '../hooks/useUser'

interface OverviewProps<T> {
    tableHooks: TableHooks<T>
    useNavV2?: boolean
}

const parseNullableChartValue = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
        return null
    }

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

export const Overview = observer(<T,>({ tableHooks, useNavV2 }: OverviewProps<T>) => {
    return <OverviewCharts params={tableHooks.params} useNavV2={useNavV2} />
})

export type ChartRequest = { endpoint: string; params: Record<string, unknown> }

type OverviewChartsProps = {
    params: Record<string, any>
    className?: string
    chartType?: string
    useNavV2?: boolean
    // Replaces the api/v5/charts request. Blended KDP metrics exist only there, so a
    // caller that passes this also loses the Blended ACoS and Blended Profit charts.
    buildRequest?: (chartParams: Record<string, any>) => ChartRequest
}

const legacyChartRequest = (chartType: string, chartParams: Record<string, any>): ChartRequest => {
    const payload = {
        type: chartType,
        ids: chartType === 'overview' ? [] : undefined,
        previous: true,
        ...chartParams,
    }

    return {
        endpoint: 'api/v5/charts',
        params: { ...payload, type: payload.type === 'ad_groups' ? 'ad-groups' : payload.type },
    }
}

export const OverviewCharts = ({
    params,
    className,
    chartType = 'overview',
    useNavV2 = false,
    buildRequest,
}: OverviewChartsProps) => {
    const { search } = useLocation()
    const shouldUseNavV2 = useNavV2 || isNavV2Enabled(search)
    const [open, setOpen] = useState(true)
    const { profile, activeProfile } = useUser()
    const [chartsLoading, setChartsLoading] = useState(true)
    const [acos, setAcos] = useState([[], [], [], []])
    const [spend, setSpend] = useState([[], [], [], []])
    const [sales, setSales] = useState([[], [], [], []])
    const [ctr, setCtr] = useState([[], [], [], []])
    const [cvr, setCvr] = useState([[], [], [], []])
    const [impressions, setImpressions] = useState([[], [], [], []])
    const [clicks, setClicks] = useState([[], [], [], []])
    const [orders, setOrders] = useState([[], [], [], []])
    const [pagesRead, setPagesRead] = useState([[], [], [], []])
    const [estimatedRoyalties, setEstimatedRoyalties] = useState([[], [], [], []])
    const [cpc, setCpc] = useState([[], [], [], []])
    const [blendedAcos, setBlendedAcos] = useState([[], [], [], []])
    const [blendedProfit, setBlendedProfit] = useState([[], [], [], []])
    const isKdp = activeProfile?.is_kdp
    const showBlended = isKdp && !buildRequest
    const latestRequest = useRef(0)
    const { page, perPage, sort, ...chartParams } = params
    const chartRequest = buildRequest
        ? buildRequest(chartParams)
        : legacyChartRequest(chartType, chartParams)
    const requestJsonString = JSON.stringify(chartRequest)
    const clearChartData = () => {
        const emptySeries = () => [[], [], [], []]
        setAcos(emptySeries())
        setSpend(emptySeries())
        setSales(emptySeries())
        setCtr(emptySeries())
        setCvr(emptySeries())
        setImpressions(emptySeries())
        setClicks(emptySeries())
        setOrders(emptySeries())
        setPagesRead(emptySeries())
        setEstimatedRoyalties(emptySeries())
        setCpc(emptySeries())
        setBlendedAcos(emptySeries())
        setBlendedProfit(emptySeries())
    }

    useEffect(() => {
        const request = ++latestRequest.current

        if (!profile) {
            clearChartData()
            setChartsLoading(false)
            return () => {
                latestRequest.current++
            }
        }
        setChartsLoading(true)

        let requestPromise: Promise<any>
        try {
            requestPromise = Promise.resolve(
                adManagerPerformanceGet(chartRequest.endpoint, chartRequest.params, true),
            )
        } catch (error) {
            requestPromise = Promise.reject(error)
        }

        requestPromise
            .then((res) => {
                if (request !== latestRequest.current) {
                    return
                }

                const currentTimestamps = res.data.current.map((i) =>
                    parseInt(`${parseDate(i.date).getTime() / 1000}`, 10),
                )
                const previousTimestamps = res.data.previous.map((i) =>
                    parseInt(`${parseDate(i.date).getTime() / 1000}`, 10),
                )

                setAcos([
                    currentTimestamps,
                    res.data.current.map((i) => parseNullableChartValue(i.acos)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseNullableChartValue(i.acos)),
                ])
                setBlendedAcos([
                    currentTimestamps,
                    res.data.current.map((i) => parseNullableChartValue(i.blended_acos)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseNullableChartValue(i.blended_acos)),
                ])
                setBlendedProfit([
                    currentTimestamps,
                    res.data.current.map((i) => parseNullableChartValue(i.blended_profit)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseNullableChartValue(i.blended_profit)),
                ])
                setSpend([
                    currentTimestamps,
                    res.data.current.map((i) => parseFloat(i.cost)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseFloat(i.cost)),
                ])
                setCtr([
                    currentTimestamps,
                    res.data.current.map((i) => parseNullableChartValue(i.ctr)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseNullableChartValue(i.ctr)),
                ])
                setCvr([
                    currentTimestamps,
                    res.data.current.map((i) => parseNullableChartValue(i.cvr)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseNullableChartValue(i.cvr)),
                ])
                setImpressions([
                    currentTimestamps,
                    res.data.current.map((i) => parseInt(i.impressions, 10)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseInt(i.impressions, 10)),
                ])
                setClicks([
                    currentTimestamps,
                    res.data.current.map((i) => parseInt(i.clicks, 10)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseInt(i.clicks, 10)),
                ])
                setOrders([
                    currentTimestamps,
                    res.data.current.map((i) => parseInt(i.orders, 10)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseInt(i.orders, 10)),
                ])
                setSales([
                    currentTimestamps,
                    res.data.current.map((i) => parseFloat(i.sales)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseFloat(i.sales)),
                ])
                setCpc([
                    currentTimestamps,
                    res.data.current.map((i) => parseNullableChartValue(i.cpc)),
                    previousTimestamps,
                    res.data.previous.map((i) => parseNullableChartValue(i.cpc)),
                ])
                if (activeProfile?.is_kdp) {
                    setPagesRead([
                        currentTimestamps,
                        res.data.current.map((i) => parseFloat(i.pages_read)),
                        previousTimestamps,
                        res.data.previous.map((i) => parseFloat(i.pages_read)),
                    ])
                    setEstimatedRoyalties([
                        currentTimestamps,
                        res.data.current.map((i) => parseFloat(i.estimated_royalties)),
                        previousTimestamps,
                        res.data.previous.map((i) => parseFloat(i.estimated_royalties)),
                    ])
                }
            })
            .catch((error) => {
                if (request === latestRequest.current) {
                    clearChartData()
                    console.error('Failed to load chart data', error)
                }
            })
            .finally(() => {
                if (request === latestRequest.current) {
                    setChartsLoading(false)
                }
            })

        return () => {
            if (request === latestRequest.current) {
                latestRequest.current++
            }
        }
    }, [profile, requestJsonString, isKdp])

    const calculateOverviewTotals = () => {
        const sumArray = (arr: number[]) => arr.reduce((sum, val) => sum + (val || 0), 0)

        const currentSpend = sumArray(spend[1])
        const currentSales = sumArray(sales[1])
        const currentClicks = sumArray(clicks[1])
        const currentImpressions = sumArray(impressions[1])
        const currentOrders = sumArray(orders[1])

        const previousSpend = sumArray(spend[3])
        const previousSales = sumArray(sales[3])
        const previousClicks = sumArray(clicks[3])
        const previousImpressions = sumArray(impressions[3])
        const previousOrders = sumArray(orders[3])

        const currentAcos = currentSales > 0 ? (currentSpend / currentSales) * 100 : 0
        const currentCpc = currentClicks > 0 ? currentSpend / currentClicks : 0
        const currentCtr = currentImpressions > 0 ? (currentClicks / currentImpressions) * 100 : 0
        const currentCvr = currentClicks > 0 ? (currentOrders / currentClicks) * 100 : 0

        const previousAcos = previousSales > 0 ? (previousSpend / previousSales) * 100 : 0
        const previousCpc = previousClicks > 0 ? previousSpend / previousClicks : 0
        const previousCtr =
            previousImpressions > 0 ? (previousClicks / previousImpressions) * 100 : 0
        const previousCvr = previousClicks > 0 ? (previousOrders / previousClicks) * 100 : 0

        let kdpTotals = {}
        if (activeProfile?.is_kdp) {
            const currentPagesRead = sumArray(pagesRead[1])
            const currentEstimatedRoyalties = sumArray(estimatedRoyalties[1])
            const previousPagesRead = sumArray(pagesRead[3])
            const previousEstimatedRoyalties = sumArray(estimatedRoyalties[3])

            const currentRpc = currentClicks > 0 ? currentEstimatedRoyalties / currentClicks : 0
            const previousRpc = previousClicks > 0 ? previousEstimatedRoyalties / previousClicks : 0

            let currentBlendedAcos = 0
            let previousBlendedAcos = 0
            let currentBlendedProfit = 0
            let previousBlendedProfit = 0

            if (activeProfile.impact_multiplier) {
                currentBlendedProfit = sumArray(blendedProfit[1])
                previousBlendedProfit = sumArray(blendedProfit[3])

                currentBlendedAcos =
                    currentBlendedProfit > 0 ? (currentSpend / currentBlendedProfit) * 100 : 0
                previousBlendedAcos =
                    previousBlendedProfit > 0 ? (previousSpend / previousBlendedProfit) * 100 : 0
            } else {
                currentBlendedAcos = currentSales > 0 ? (currentSpend / currentSales) * 100 : 0
                previousBlendedAcos = previousSales > 0 ? (previousSpend / previousSales) * 100 : 0
                currentBlendedProfit = currentSales
                previousBlendedProfit = previousSales
            }

            kdpTotals = {
                pages_read: { current: currentPagesRead, previous: previousPagesRead },
                estimated_royalties: {
                    current: currentEstimatedRoyalties,
                    previous: previousEstimatedRoyalties,
                },
                rpc: { current: currentRpc, previous: previousRpc },
                blended_acos: { current: currentBlendedAcos, previous: previousBlendedAcos },
                blended_profit: { current: currentBlendedProfit, previous: previousBlendedProfit },
            }
        }

        return {
            cost: { current: currentSpend, previous: previousSpend },
            sales: { current: currentSales, previous: previousSales },
            clicks: { current: currentClicks, previous: previousClicks },
            impressions: { current: currentImpressions, previous: previousImpressions },
            orders: { current: currentOrders, previous: previousOrders },
            acos: { current: currentAcos, previous: previousAcos },
            cpc: { current: currentCpc, previous: previousCpc },
            ctr: { current: currentCtr, previous: previousCtr },
            cvr: { current: currentCvr, previous: previousCvr },
            ...kdpTotals,
        }
    }

    const calculatedOverview = calculateOverviewTotals()

    const charts = [
        {
            title: showBlended ? 'Blended ACoS' : 'ACoS',
            lowerIsBetter: true,
            visible: true,
            overview: {
                current: showBlended
                    ? calculatedOverview.blended_acos?.current
                    : calculatedOverview.acos?.current,
                previous: showBlended
                    ? calculatedOverview.blended_acos?.previous
                    : calculatedOverview.acos?.previous,
            },
            formatter: percentage,
            data: showBlended ? blendedAcos : acos,
        },
        {
            title: 'Spend',
            overview: {
                current: calculatedOverview.cost?.current,
                previous: calculatedOverview.cost?.previous,
            },
            formatter: money,
            data: spend,
        },
        {
            title: 'Sales',
            overview: {
                current: calculatedOverview.sales?.current,
                previous: calculatedOverview.sales?.previous,
            },
            formatter: money,
            data: sales,
        },
        {
            title: 'Orders',
            overview: {
                current: calculatedOverview.orders?.current,
                previous: calculatedOverview.orders?.previous,
            },
            formatter: number,
            data: orders,
        },
        {
            title: 'CPC',
            overview: {
                current: calculatedOverview.cpc?.current,
                previous: calculatedOverview.cpc?.previous,
            },
            formatter: money,
            data: cpc,
        },
        {
            title: 'Clicks',
            overview: {
                current: calculatedOverview.clicks?.current,
                previous: calculatedOverview.clicks?.previous,
            },
            formatter: number,
            data: clicks,
        },
        {
            title: 'Impressions',
            overview: {
                current: calculatedOverview.impressions?.current,
                previous: calculatedOverview.impressions?.previous,
            },
            formatter: number,
            data: impressions,
        },
        {
            title: 'CTR',
            overview: {
                current: calculatedOverview.ctr?.current,
                previous: calculatedOverview.ctr?.previous,
            },
            formatter: percentage,
            data: ctr,
        },
        {
            title: 'CVR',
            overview: {
                current: calculatedOverview.cvr?.current,
                previous: calculatedOverview.cvr?.previous,
            },
            formatter: percentage,
            data: cvr,
        },
        {
            title: 'Pages Read',
            visible: activeProfile?.is_kdp,
            overview: {
                current: calculatedOverview.pages_read?.current,
                previous: calculatedOverview.pages_read?.previous,
            },
            formatter: number,
            data: pagesRead,
        },
        {
            title: 'Estimated Royalties',
            visible: activeProfile?.is_kdp,
            overview: {
                current: calculatedOverview.estimated_royalties?.current,
                previous: calculatedOverview.estimated_royalties?.previous,
            },
            formatter: money,
            data: estimatedRoyalties,
        },
        ...(buildRequest
            ? []
            : [
                  {
                      title: 'Blended Profit',
                      visible: activeProfile?.is_kdp,
                      overview: {
                          current: calculatedOverview.blended_profit?.current,
                          previous: calculatedOverview.blended_profit?.previous,
                      },
                      formatter: money,
                      data: blendedProfit,
                  },
              ]),
    ]

    const memoizedGroupedCharts = useMemo(() => {
        if (shouldUseNavV2 || open) {
            return (
                <GroupedCharts
                    loading={chartsLoading}
                    charts={charts}
                    className={shouldUseNavV2 ? '!mt-0' : undefined}
                    showFirstRowTopBorder={!shouldUseNavV2}
                />
            )
        } else {
            return <span />
        }
    }, [
        chartsLoading,
        charts,
        acos,
        spend,
        sales,
        ctr,
        cvr,
        impressions,
        clicks,
        orders,
        cpc,
        pagesRead,
        estimatedRoyalties,
        calculatedOverview,
        open,
        shouldUseNavV2,
    ])

    return (
        <Card
            className={classNames(
                'flex flex-col p-0',
                shouldUseNavV2
                    ? '!rounded-none !shadow-none border-b border-[#E5E7EB]'
                    : classNames('bp4-card-rounded', className),
            )}
        >
            {!shouldUseNavV2 && (
                <div
                    className={classNames('pl-4 pt-2 text-lg flex items-center cursor-pointer', {
                        'pb-2': !open,
                    })}
                    onClick={() => setOpen((prev) => !prev)}
                >
                    <span>Charts</span>
                    <Icon
                        icon={
                            open ? (
                                <Icon icon="chevron-down" className="ml-2" color={Colors.GRAY3} />
                            ) : (
                                <Icon icon="chevron-right" className="ml-2" color={Colors.GRAY3} />
                            )
                        }
                    />
                </div>
            )}
            {memoizedGroupedCharts}
        </Card>
    )
}
